/**
 * The dealer workspace's Inventory slice of the data provider (DataProvider.inventory): the store's listed vehicles, one
 * vehicle with its history and what callers hear about it, the feed's settings and health, uploads (check, then apply),
 * runs and the quarantine release, over the inventory routes (switch inventory_feed; the API answers 404
 * feature_disabled while it is off). Live only: the fixture tenant is a workshop, so its slice answers "not switched on".
 *   /v1/app/inventory/vehicles*  /v1/app/inventory/feed*  /v1/app/inventory/uploads*  /v1/app/inventory/runs*
 */
import { APP, ApiError, type Http } from '@app/core/http';
import type { Condition, FeedKind, Health, HistoryRow, RunStatus, UnitStatus, VehicleQuery } from './inventoryModel';

type UUID = string;
type ISO = string;

export interface Unit {
  id: UUID;
  stock_number: string;
  vin: string | null;
  vin_valid: boolean;
  condition: Condition | null;
  year: number;
  make: string;
  model: string;
  trim: string | null;
  body: string | null;
  ext_color: string | null;
  ext_color_family: string | null;
  mileage: number | null;
  price_cents: number | null;
  advertised_price_cents: number | null;
  msrp_cents: number | null;
  status: UnitStatus;
  is_hidden_from_calls: boolean;
  photo_url: string | null;
  vdp_url: string | null;
  first_seen_at: ISO;
  days_on_lot: number | null;
  version: number;
}
export interface UnitDetail extends Unit {
  drivetrain: string | null;
  engine: string | null;
  fuel: string | null;
  transmission: string | null;
  int_color: string | null;
  last_seen_at: ISO;
  status_changed_at: ISO;
  decode: {
    make: string | null;
    model: string | null;
    year: number | null;
    body: string | null;
    drivetrain: string | null;
    fuel: string | null;
    engine: string | null;
    mismatches: string[];
  } | null;
  history: HistoryRow[];
  caller_line: { en: string; es: string } | null;
  calls: { id: UUID; answered_at: ISO; outcome: string; caller_name: string | null }[];
}
export interface Mapping {
  headers?: Record<string, string>;
  filter?: { column: string; equals: string } | null;
  condition_map?: Record<string, Condition>;
  status_map?: Record<string, UnitStatus>;
  permission_ref?: string;
}
export interface Feed {
  id: UUID;
  version: number;
  kind: FeedKind;
  label: string | null;
  preset: string;
  mapping: Mapping;
  url: string | null;
  interval_minutes: number;
  stale_after_minutes: number;
  speak_prices_enabled: boolean;
  price_field: string | null;
  in_transit_spoken: boolean;
  sftp: { host: string | null; port: number | null; username: string | null; has_password: boolean; has_pubkey: boolean } | null;
  last_success_at: ISO | null;
}
export interface FeedState {
  feed: Feed | null;
  health: Health;
  presets: string[];
  fields: string[];
}
export interface FeedPut {
  kind: FeedKind;
  label?: string | null;
  preset: string;
  mapping?: Mapping;
  url?: string | null;
  interval_minutes?: number;
  stale_after_minutes?: number;
  speak_prices_enabled?: boolean;
  price_field?: string | null;
  in_transit_spoken?: boolean;
  sftp_pubkey?: string | null;
  version?: number;
}
export interface RunReport {
  headers?: { header: string; field: string | null; sample: string | null }[];
  errors?: { row: number; field: string | null; code: string }[];
  duplicates?: { row: number; key: string; value: string }[];
  required_missing?: string[];
}
export interface Run {
  id: UUID;
  status: RunStatus;
  source?: string;
  file_name: string | null;
  started_at?: ISO;
  finished_at?: ISO | null;
  rows_read: number;
  rows_valid: number;
  added: number;
  changed: number;
  unchanged_rows: number;
  missing: number;
  removed: number;
  sold: number;
  quarantine_reason: string | null;
  error_code: string | null;
  report?: RunReport;
}

export interface Api {
  vehicles(q: VehicleQuery): Promise<{ items: Unit[]; next: string | null }>;
  vehicle(id: UUID): Promise<UnitDetail>;
  setHidden(id: UUID, hidden: boolean, version: number): Promise<UnitDetail>;
  feed(): Promise<FeedState>;
  putFeed(body: FeedPut): Promise<Feed>;
  sftpCredential(): Promise<{ host: string | null; port: number | null; username: string; password: string }>;
  upload(file: File): Promise<{ upload_id: UUID; run: Run }>;
  apply(uploadId: UUID, key: string): Promise<Run>;
  runs(cursor?: string | null): Promise<{ items: Run[]; next: string | null }>;
  run(id: UUID): Promise<Run>;
  release(id: UUID, key: string): Promise<Run>;
}

const I = `${APP}/inventory`;
const e = encodeURIComponent;

export const live = (http: Http): Api => ({
  vehicles: (q) => http.get(`${I}/vehicles`, { query: { ...q } }),
  vehicle: (id) => http.get<{ vehicle: UnitDetail }>(`${I}/vehicles/${e(id)}`).then((r) => r.vehicle),
  setHidden: (id, hidden, version) =>
    http
      .patch<{ vehicle: UnitDetail }>(`${I}/vehicles/${e(id)}`, { is_hidden_from_calls: hidden, version }, { version })
      .then((r) => r.vehicle),
  feed: () => http.get(`${I}/feed`),
  putFeed: (body) => http.put<{ feed: Feed }>(`${I}/feed`, body).then((r) => r.feed),
  sftpCredential: () => http.post(`${I}/feed/sftp-credential`, {}),
  upload: (file) => {
    const fd = new FormData();
    fd.append('file', file, file.name);
    return http.post(`${I}/uploads`, fd);
  },
  apply: (id, key) => http.post<{ run: Run }>(`${I}/uploads/${e(id)}/apply`, {}, { idempotencyKey: key }).then((r) => r.run),
  runs: (cursor) => http.get(`${I}/runs`, { query: { cursor: cursor ?? undefined } }),
  run: (id) => http.get<{ run: Run }>(`${I}/runs/${e(id)}`).then((r) => r.run),
  release: (id, key) => http.post<{ run: Run }>(`${I}/runs/${e(id)}/release`, {}, { idempotencyKey: key }).then((r) => r.run),
});

/** The fixture tenant is a workshop: every inventory call answers as the API does while the switch is off. */
export const fixture = (): Api => {
  const off = () => Promise.reject(new ApiError(404, 'feature_disabled', null, { ok: false, error: 'feature_disabled' }));
  return new Proxy({} as Api, { get: () => off });
};
